import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeft, Image, Mic, Send } from 'lucide-react';
import { socket } from '../../admin/chat/socket';
import ChatViewHelpers from './ChatViewHelpers';
import MessageBubble from './MessageBubble';
import MediaModal from './MediaModal';
import { resolveMediaType } from './chatUtils';

function ChatView({ chatId, onBackClick, userId, setActiveComponent }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sessionOpen, setSessionOpen] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [deliveryPointID, setDeliveryPointID] = useState(null);
  const endRef = useRef(null);

  const normalizedUserId = userId == null ? null : Number(String(userId).trim());
  const isAdmin = normalizedUserId === 2;

  const scrollToBottom = () => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    if (chatId == null) return;

    const currentId = Number(chatId);
    const room = `chat_${currentId}`;

    // Fetch messages
    fetch(`${process.env.REACT_APP_API_URL}/api/admin/chats/${currentId}/messages`)
      .then(r => r.json())
      .then(data => {
        setMessages(data);
        if (Array.isArray(data) && data.length) {
          const candidate = data[0].phone_number ?? data[0].from ?? data[0].sender_phone ?? null;
          if (candidate) setPhoneNumber(candidate);
        }
      })
      .catch(err => console.error('Fetch messages error:', err));

    // Fetch session status
    fetch(`${process.env.REACT_APP_API_URL}/api/admin/chats/${currentId}/session`)
      .then(r => r.json())
      .then(data => setSessionOpen(!!data.session_open))
      .catch(err => console.error('Fetch session error:', err));

    // Join socket room
    const doJoin = () => {
      socket.emit('admin_join_chat', room, (ack) => {
        console.log('[socket] join ack:', ack);
      });
    };

    if (socket.connected) {
      doJoin();
    } else {
      const onConnect = () => doJoin();
      socket.once('connect', onConnect);
    }

    // Listen for new messages
    const onNewMessage = (msg) => {
      const incomingId = Number(msg?.chat_id);
      if (incomingId !== currentId) return;
      setMessages(prev => [...prev, msg]);
      setPhoneNumber(prev => prev || msg.phone_number || msg.from || msg.sender_phone || null);
    };

    socket.on('new_message', onNewMessage);

    return () => {
      socket.off('new_message', onNewMessage);
      socket.emit('admin_leave_chat', room);
    };
  }, [chatId]);

  // Lookup delivery point by phone
  useEffect(() => {
    if (!phoneNumber) return;

    const controller = new AbortController();
    
    (async () => {
      try {
        const url = `${process.env.REACT_APP_API_URL}/api/delivery_points/lookup_by_phone?phone=${encodeURIComponent(phoneNumber)}`;
        const res = await fetch(url, { signal: controller.signal });
        
        if (!res.ok) {
          if (res.status === 404) {
            setDeliveryPointID(null);
            return;
          }
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        setDeliveryPointID(data?.delivery_point_id ?? null);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Lookup error:', err);
          setDeliveryPointID(null);
        }
      }
    })();

    return () => controller.abort();
  }, [phoneNumber]);

  function send() {
    if (!draft.trim()) return;
    
    fetch(`${process.env.REACT_APP_API_URL}/api/admin/chats/${chatId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: draft })
    });
    
    setDraft('');
  }

  function sendTemplate() {
    fetch(`${process.env.REACT_APP_API_URL}/api/admin/chats/${chatId}/send_de_camino`, { 
      method: 'POST' 
    })
      .then(r => r.json())
      .then(console.log)
      .catch(console.error);
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  // Light background pattern
  const backgroundPattern = `
    <svg width="120" height="120" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="chat-pattern-light" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
          <circle cx="15" cy="15" r="2" fill="#B1336E" opacity="0.04"/>
          <circle cx="60" cy="35" r="1.5" fill="#E6B43C" opacity="0.03"/>
          <circle cx="95" cy="70" r="2.5" fill="#67A43C" opacity="0.03"/>
          <circle cx="35" cy="90" r="1.8" fill="#B1336E" opacity="0.04"/>
          <circle cx="80" cy="100" r="2.2" fill="#E6B43C" opacity="0.02"/>
        </pattern>
      </defs>
      <rect width="120" height="120" fill="url(#chat-pattern-light)"/>
    </svg>
  `;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex-shrink-0 h-16 flex items-center gap-3 px-4 bg-gradient-to-r from-white via-brand-primary/5 to-white border-b border-slate-200 shadow-sm">
        <button
          onClick={() => setActiveComponent('Options')}
          className="p-2 hover:bg-slate-100 rounded-xl transition-all text-slate-700 hover:text-brand-primary"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="font-extrabold text-slate-900 font-montserrat truncate">
            {phoneNumber}
          </div>
          <div className="text-xs text-slate-500 font-montserrat">
            {sessionOpen ? '🟢 Sesión activa' : '⚪ Sesión cerrada'}
          </div>
        </div>
        
        {isAdmin && deliveryPointID && (
          <div className="flex-shrink-0">
            <ChatViewHelpers deliveryPointID={deliveryPointID} />
          </div>
        )}
      </div>

      {/* Messages Area with Light Pattern */}
      <div 
        className="flex-1 overflow-y-auto p-4 bg-slate-50 relative"
        style={{
          backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(backgroundPattern)}")`,
          backgroundSize: '120px 120px',
          backgroundRepeat: 'repeat'
        }}
      >
        <div className="relative z-10">
          {messages.map(m => (
            <MessageBubble 
              key={m.id} 
              message={m} 
              mediaType={resolveMediaType(m)}
            />
          ))}
          <div ref={endRef} />
        </div>
      </div>

      {/* Composer */}
      <div className="flex-shrink-0 p-3 bg-white border-t border-slate-200 shadow-lg">
        {sessionOpen ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowMediaModal(true)}
              className="p-2.5 bg-slate-100 border border-slate-200 text-brand-gold rounded-xl hover:bg-slate-200 hover:scale-105 transition-all"
              title="Enviar imagen"
            >
              <Image className="w-5 h-5" />
            </button>

            <button
              onClick={() => {}}
              disabled
              className="p-2.5 bg-slate-100 border border-slate-200 text-slate-400 rounded-xl cursor-not-allowed opacity-50"
              title="Audio deshabilitado temporalmente"
            >
              <Mic className="w-5 h-5" />
            </button>

            <input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe un mensaje..."
              className="flex-1 bg-slate-50 border border-slate-200 text-slate-900 rounded-xl px-4 py-3 outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 transition-all font-montserrat placeholder-slate-400"
            />

            <button
              onClick={send}
              className="p-3 bg-brand-green text-white rounded-xl font-semibold hover:bg-brand-green/90 hover:scale-105 transition-all shadow-md"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        ) : isAdmin ? (
          <button
            onClick={sendTemplate}
            className="w-full bg-gradient-to-r from-brand-green to-brand-green/90 text-white rounded-xl py-3.5 font-bold hover:from-brand-green/90 hover:to-brand-green hover:scale-[1.02] transition-all shadow-md font-montserrat"
          >
            📨 Enviar plantilla de "Saludo"
          </button>
        ) : (
          <button
            disabled
            className="w-full bg-slate-100 text-slate-500 border-2 border-slate-200 rounded-xl py-3.5 font-semibold cursor-not-allowed font-montserrat"
          >
            🔒 Chat cerrado - Solo admin puede enviar plantillas
          </button>
        )}
      </div>

      {/* Media Modal */}
      {showMediaModal && (
        <MediaModal
          chatId={chatId}
          onClose={() => setShowMediaModal(false)}
        />
      )}
    </div>
  );
}

export default ChatView;